import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@stores/auth-store';
import { useEffect, useRef, useState } from 'react';
import { tokenRefreshManager } from '@libs/api/token/refresh';
import * as ToastUtil from '@utils/toast-utils';

interface ProtectedRouteProps {
  allowedRoles?: string[];
}

export function AuthRoute({ allowedRoles }: Readonly<ProtectedRouteProps>) {
  const { user } = useAuthStore();
  const location = useLocation();
  const hasAlerted = useRef(false);
  const [isCheckingRefresh, setIsCheckingRefresh] = useState(false);

  // 리프레시 중인지 확인
  useEffect(() => {
    const refreshing = tokenRefreshManager.getIsRefreshing();
    setIsCheckingRefresh(refreshing);

    if (!refreshing) {
      return;
    }

    // 리프레시 중일 때는 주기적으로 확인
    const interval = setInterval(() => {
      const stillRefreshing = tokenRefreshManager.getIsRefreshing();
      setIsCheckingRefresh(stillRefreshing);

      if (!stillRefreshing) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [user, location.pathname]);

  if (['/signIn', '/signUp'].includes(location.pathname)) {
    return;
  }

  if (!user) {
    // 리프레시 중이면 잠시 대기 (리프레시가 완료되면 user가 업데이트됨)
    if (isCheckingRefresh || tokenRefreshManager.getIsRefreshing()) {
      return null; // 로딩 상태 표시 또는 null
    }

    if (location.pathname === '/') {
      return <Navigate to="/sign-in" replace />;
    }

    // api.ts에서 로그인 만료 관련 alert를 이미 표시했다면 여기서는 표시하지 않음
    if (!tokenRefreshManager.getHasShownExpirationAlert() && !hasAlerted.current) {
      hasAlerted.current = true;
      // 일반적인 권한 체크를 위한 alert (직접 페이지 접근 시)
      ToastUtil.warning('해당 페이지는 로그인 후 사용 가능합니다.');
    }
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role.toUpperCase())) {
    // 20250624, 권의진, 경고메세지 한번만 뜨게 변경
    if (!hasAlerted.current) {
      hasAlerted.current = true;
      ToastUtil.warning('현재 로그인한 계정의 권한으로는 사용할 수 없는 페이지입니다.');
    }

    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
